import _ from 'lodash';
import { useEffect, useState } from 'react';
import { Button, Row, Col, Form, Card, ListGroup, Alert } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import store from 'store';
import { AxiosResponse } from 'axios';
import ApiService from '../../services/ApiService';
import PaymentCardTransaction from './PaymentCardTransaction';
import PaymentCardCharge from './PaymentCardCharge';
import {
  CardTransaction,
  CashTransaction,
  Customer,
  Invoice,
  PaymentDepartment,
  PaymentMethod,
  PaymentSession,
  PaymentSessionCancelResponse,
  PaymentSessionSuccessResponse,
  TransactionSummary,
} from './types';
import { fetchCashInvoicePrinters } from '../../util/payment';
import { modal } from '../modal/ModalEmitter';
import CardPayment from './CardPayment';
import CashPayment from './CashPayment';
import toastr from '../toastr';
import PaymentCashTransaction from './PaymentCashTransaction';
import PaymentCashCollect from './PaymentCashCollect';
import { ClipLoader } from 'react-spinners';
import CreatableSelect from 'react-select/creatable';

interface PaymentTerminal {
  id: string;
  name: string;
  branchId: number;
}

interface Printer {
  id: string;
  name: string;
}

interface BaseProps {
  action?: 'sale' | 'credit';
  amount: number;
  cardTransactions?: CardTransaction[];
  cashTransactions?: CashTransaction[];
  cashInvoicePrinterId?: string | null;
  customerNum: string;
  department: PaymentDepartment;
  enabledHostedPayments?: boolean;
  noCash?: boolean;
  onCancel?: (response: PaymentSessionCancelResponse) => void;
  onResumeLater?: () => void;
  onSuccess: (response: PaymentSessionSuccessResponse) => void;
  readOnly?: boolean;
  selectCashInvoicePrinter?: boolean;
  sessionId?: string;
  terminalId?: number | string;
  userInitials?: string;
}

interface SingleInvoiceProps extends BaseProps {
  invoiceNum: string;
  originalInvoiceNum?: string;
  invoices?: never;
}

interface MultiInvoiceProps extends BaseProps {
  invoiceNum?: never;
  originalInvoiceNum?: never;
  invoices: Invoice[];
}

interface PaymentAccount {
  id: number;
  name: string;
  description: string;
}

type Props = SingleInvoiceProps | MultiInvoiceProps;

const defaultTerminalKey = 'payment.defaultTerminalId';
const defaultMethodKey = 'payment.defaultMethod';
const defaultPaymentAccountKey = 'payment.defaultAccountId';

const defaultSummaryEntry = {
  address: '',
  amount: '',
  cardBin: '',
  cardEntry: '',
  cardExp: '',
  cardLast4: '',
  cardType: '',
  firstName: '',
  gateway: '',
  hash: '',
  lastName: '',
  signature: '',
  terminalName: '',
  token: '',
  transactionId: '',
  zip: '',
};

const PaymentSaleForm = ({
  action = 'sale',
  amount = 0,
  cardTransactions = [],
  cashTransactions = [],
  cashInvoicePrinterId = null,
  customerNum,
  enabledHostedPayments = true,
  department,
  invoiceNum,
  invoices = [],
  noCash = false,
  onCancel,
  onResumeLater,
  onSuccess,
  originalInvoiceNum,
  readOnly = false,
  selectCashInvoicePrinter = false,
  sessionId: sessionIdProp = '',
  terminalId,
  userInitials: userInitialsProp = '',
}: Props) => {
  const user = useSelector((state: any) => state.app.user);
  const branch = useSelector((state: any) => state.app.branch);
  const [activeCashInvoicePrinterId, setActiveCashInvoicePrinterId] = useState<string | null>(cashInvoicePrinterId);
  const [activeTerminalId, setActiveTerminalId] = useState(String(terminalId || store.get(defaultTerminalKey) || ''));
  const [canceling, setCanceling] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [cashInvoicePrinters, setCashInvoicePrinters] = useState<Printer[]>([]);
  const [customer, setCustomer] = useState<Customer | null>(null);
  const [paymentSession, setPaymentSession] = useState<PaymentSession>();
  const [ready, setReady] = useState(false);
  const [sessionId, setSessionId] = useState(sessionIdProp);
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [showCashForm, setShowCashForm] = useState(false);
  const [terminals, setTerminals] = useState<PaymentTerminal[]>([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [userInitials, setUserInitials] = useState(userInitialsProp || user.ppc || '');
  const [hostedEmail, setHostedEmail] = useState('');
  const [hostedEmailOptions, setHostedEmailOptions] = useState<string[]>([]);
  const [paymentAccountId, setPaymentAccountId] = useState(store.get(defaultPaymentAccountKey) || '');
  const [paymentAccounts, setPaymentAccounts] = useState<PaymentAccount[]>([]);
  const [cardPayments, setCardPayments] = useState<CardPayment[]>(
    _.orderBy(cardTransactions, ['id'], ['asc']).map(p => new CardPayment(p))
  );
  const [cashPayments, setCashPayments] = useState<CashPayment[]>(
    _.orderBy(cashTransactions, ['id'], ['asc']).map(p => new CashPayment(p))
  );

  const hasTerminalSupport = () => {
    return branch.payment && branch.payment.enabled && branch.payment.resellerName;
  };

  const hasCardNotPresentSupport = () => {
    return branch.payment && branch.payment.enabled && branch.payment.publicKey;
  };

  // Hosted payments can be switched on/off with a prop and branch level param
  const hasHostedSupport = () => !!_.get(branch, 'payment.hosted.enabled') && enabledHostedPayments;

  const getDefaultPaymentMethod = () => {
    const supportedMethods = {
      terminal: hasTerminalSupport(),
      cardNotPresent: hasCardNotPresentSupport(),
      hosted: hasHostedSupport(),
    };

    // try to set last used method
    const storedMethod = store.get(defaultMethodKey);
    if (storedMethod && supportedMethods[storedMethod]) {
      return storedMethod;
    }

    return hasTerminalSupport() ? 'terminal' : 'cardNotPresent';
  };

  const [method, setMethod] = useState<PaymentMethod>(getDefaultPaymentMethod());

  const getInvoiceList = () => {
    if (invoiceNum) {
      const invoiceObj: Invoice = { invoiceNum, amount };
      if (originalInvoiceNum) {
        invoiceObj.originalInvoiceNum = originalInvoiceNum;
      }
      return [invoiceObj];
    } else {
      return invoices;
    }
  };

  const getOutstandingAmount = () => parseFloat((amount - totalPaid).toFixed(2));

  const getNextRefId = () => `${sessionId}-${Date.now().toString(16)}`.toUpperCase();

  const isCredit = () => action === 'credit';
  const isSessionClosed = () => !!(paymentSession && paymentSession.status !== 'open');
  const isPaid = () => totalPaid >= amount;

  const getCashAmount = () => {
    let cashAmount = 0;
    for (const cashPayment of cashPayments) {
      cashAmount += cashPayment.amount;
    }

    return parseFloat(cashAmount.toFixed(2));
  };

  const createPaymentSession = () => {
    return ApiService.post('/payment/session', {
      sessionId,
      customerNum,
      department,
      invoices: getInvoiceList(),
      customer,
      userInitials,
      printerId: cashInvoicePrinterId,
    }).then(response => {
      setPaymentSession(response.data);
      return Promise.resolve(response);
    });
  };

  const getTotalRefundedForSession = () => {
    let total = 0;
    for (const payment of cardPayments) {
      if (
        payment.sessionId === sessionId &&
        ['void', 'refund'].includes(payment.transactionType) &&
        payment.isApproved()
      ) {
        total += Math.abs(payment.amount);
      }
    }

    for (const payment of cashPayments) {
      if (payment.sessionId === sessionId && payment.transactionType === 'refund') {
        total += Math.abs(payment.amount);
      }
    }

    return parseFloat(total.toFixed(2));
  };

  const getOutstandingCreditAmount = () => {
    if (!amount) {
      return;
    }

    return parseFloat((Math.abs(amount) - getTotalRefundedForSession()).toFixed(2));
  };

  const pushCardTransaction = async (transaction: CardTransaction) => {
    if (!paymentSession) {
      ApiService.get(`/payment/session/${sessionId}`).then((response: AxiosResponse) => {
        setPaymentSession(response.data);
      });
    }

    setCardPayments(oldValue => {
      const newArray = [...oldValue];

      // check if tx exists and update it
      for (const [i, tx] of newArray.entries()) {
        if (tx.id === transaction.id) {
          newArray[i] = new CardPayment(transaction);
          return newArray;
        }
      }

      return [...oldValue, new CardPayment(transaction)];
    });
  };

  const pushCashTransaction = (transaction: CashTransaction) => {
    if (!paymentSession) {
      ApiService.get(`/payment/session/${sessionId}`).then((response: AxiosResponse) => {
        setPaymentSession(response.data);
      });
    }

    setCashPayments(oldValue => {
      const newArray = [...oldValue];
      // check if tx exists and update it
      for (const [i, tx] of newArray.entries()) {
        if (tx.id === transaction.id) {
          newArray[i] = new CashPayment(transaction);
          return newArray;
        }
      }

      return [...oldValue, new CashPayment(transaction)];
    });
  };

  const getCreditTransactionSummary = () => {
    const entries: TransactionSummary[] = [];

    for (const payment of cardPayments) {
      if (payment.sessionId !== sessionId || !payment.isApproved()) {
        continue;
      }
      const [lastName, firstName = ''] = payment.cardName.split('/');
      entries.push({
        ...defaultSummaryEntry,
        amount: -1 * payment.amount,
        cardBin: payment.cardBin,
        cardEntry: payment.cardEntry,
        cardExp: payment.cardExp,
        cardLast4: payment.cardLast4,
        cardType: payment.cardType,
        firstName,
        gateway: payment.gateway,
        lastName,
        terminalName: payment.terminalName,
        token: payment.cardToken,
        transactionId: payment.id,
        transactionResult: payment.transactionResult,
      });
    }

    for (const payment of cashPayments) {
      if (payment.sessionId !== sessionId) {
        continue;
      }
      entries.push({
        ...defaultSummaryEntry,
        amount: -1 * parseFloat(payment.amount.toFixed(2)),
        cardType: 'cash',
        transactionResult: 'approved',
      });
    }

    return entries;
  };

  const getTransactionSummary = () => {
    // positive card charges after refunds / voids
    const entries: TransactionSummary[] = [];

    for (const payment of cardPayments) {
      if (
        payment.transactionType === 'sale' &&
        payment.isApproved() &&
        !payment.isVoid() &&
        !payment.isFullyRefunded()
      ) {
        const [lastName, firstName = ''] = payment.cardName.split('/');
        entries.push({
          ...defaultSummaryEntry,
          amount: payment.getNetAmount(),
          cardBin: payment.cardBin,
          cardEntry: payment.cardEntry,
          cardExp: payment.cardExp,
          cardLast4: payment.cardLast4,
          cardType: payment.cardType,
          firstName,
          gateway: payment.gateway,
          lastName,
          saveCard: payment.saveCard || 'Y',
          signature: payment.signatureData,
          terminalName: payment.terminalName,
          token: payment.cardToken,
          transactionId: payment.id,
          transactionResult: payment.transactionResult,
        });
      }
    }

    if (getCashAmount()) {
      entries.push({
        ...defaultSummaryEntry,
        amount: getCashAmount(),
        cardType: 'cash',
        transactionResult: 'approved',
      });
    }

    return entries;
  };

  useEffect(() => {
    const promises = [
      ApiService.get('/payment/terminal').then((response: AxiosResponse) => {
        // filter out terminals not linked to active branch
        let terminalsData = response.data.filter((t: PaymentTerminal) => t.branchId === branch.id);

        setTerminals(terminalsData);

        if (!activeTerminalId && terminalsData.length) {
          setActiveTerminalId(terminalsData[0].id);
        }

        // ensure activeTerminalId is valid
        if (activeTerminalId) {
          if (!terminalsData.length) {
            setActiveTerminalId('');
          } else {
            const terminalIds = terminalsData.map((o: PaymentTerminal) => o.id);
            if (!terminalIds.includes(parseInt(activeTerminalId))) {
              setActiveTerminalId('');
            }
          }
        }
      }),
      ApiService.get(`/customer/${customerNum}`).then((response: AxiosResponse) => {
        setCustomer(response.data);
        setHostedEmail(response.data.email || '');
        setHostedEmailOptions(response.data.partInvoiceEmails || []);
      }),
      ApiService.get('payment/accounts').then(response => {
        setPaymentAccounts(response.data);
      }),
    ];

    if (selectCashInvoicePrinter) {
      promises.push(
        fetchCashInvoicePrinters().then((printers: Printer[]) => {
          setCashInvoicePrinters(printers);
          if (printers.length > 0) {
            setActiveCashInvoicePrinterId(printers[0].id);
          }
        })
      );
    }

    if (!sessionId) {
      // get a session id ready
      promises.push(
        ApiService.get('/payment/session/new').then((response: AxiosResponse) => {
          setSessionId(response.data.id);
        })
      );
    } else {
      promises.push(
        ApiService.get(`/payment/session/${sessionId}`).then((response: AxiosResponse) => {
          setPaymentSession(response.data);
        })
      );
    }

    Promise.all(promises).then(() => setReady(true));
  }, []);

  useEffect(() => {
    if (customer) {
      // add initial charge
      setShowChargeForm(true);
    }
  }, [customer]);

  useEffect(() => {
    let amount = 0;
    for (const payment of cardPayments) {
      if (payment.transactionType !== 'sale') {
        continue;
      }

      if (payment.isApproved() && !payment.isVoid()) {
        amount += payment.amount;
      }

      if (payment.hasRefunds()) {
        for (const refund of payment.refunds || []) {
          amount -= refund.amount;
        }
      }
    }

    amount += getCashAmount();

    setTotalPaid(parseFloat(amount.toFixed(2)));
  }, [cardPayments, cashPayments]);

  useEffect(() => {
    if (paymentAccountId) {
      store.set(defaultPaymentAccountKey, paymentAccountId);
    }
  }, [paymentAccountId]);

  useEffect(() => {
    if (activeTerminalId) {
      store.set(defaultTerminalKey, activeTerminalId);
    }
  }, [activeTerminalId]);

  useEffect(() => {
    store.set(defaultMethodKey, method);
  }, [method]);

  const handleError = (err: Error, { message = '' }: { message?: string } = {}) => {
    console.error(err);
    toastr.error(message || 'Request failed try again');
  };

  const handleAddCash = () => {
    // show the cash form, defaulting value to outstanding
    setShowChargeForm(false);
    setShowCashForm(true);
  };

  const handleSale = (saleTx: CardTransaction) => {
    pushCardTransaction(saleTx);
  };

  const handleVoid = ({ saleTx, voidTx }: { saleTx: CardTransaction; voidTx: CardTransaction }) => {
    pushCardTransaction(voidTx);
    pushCardTransaction(saleTx);
  };
  const handleRefund = ({ saleTx, refundTx }: { saleTx: CardTransaction; refundTx: CardTransaction }) => {
    pushCardTransaction(refundTx);
    pushCardTransaction(saleTx);
  };

  const handleCashCollect = async ({ cashTx }: { cashTx: CashTransaction }) => {
    pushCashTransaction(cashTx);
  };

  const handleCashRefund = async ({ saleTx, refundTx }: { saleTx: CashTransaction; refundTx: CashTransaction }) => {
    pushCashTransaction(saleTx);
    pushCashTransaction(refundTx);
  };

  const handleCancel = () => {
    modal.confirm({
      title: 'Cancel Transaction',
      message: 'This will cancel the sale and refund all charges!',
      ok: async () => {
        setCanceling(true);
        modal.close(); // close confirm

        try {
          if (!isCredit()) {
            if (getCashAmount()) {
              for (const payment of cashPayments) {
                // TODO SHOW THE ANIMATION OR THE HELL WITH IT?
                await payment.doFullRefund({ userInitials, sessionId });
              }
            }

            for (const payment of cardPayments) {
              await payment.doVoidOrRefundPayment({ userInitials, sessionId });
            }
          }

          modal.close(); // close self

          if (onCancel) {
            onCancel({ sessionId, invoices: getInvoiceList() });
          }
        } catch (err) {
          toastr.error('Something went wrong, try again later');
          console.error(err);
        }

        setCanceling(false);
      },
    });
  };

  const handleResume = ({ checkPaymentSession = true }: { checkPaymentSession?: boolean } = {}) => {
    // ensure session exists
    if (checkPaymentSession && !paymentSession) {
      if (!userInitials) {
        toastr.error('Set User Initials');
        return;
      }

      createPaymentSession()
        .then(() => {
          modal.close();
          if (onResumeLater) {
            onResumeLater();
          }
        })
        .catch(handleError);
    } else {
      modal.close();
      if (onResumeLater) {
        onResumeLater();
      }
    }
  };

  const handleHostedEmail = async () => {
    if (emailing) {
      return;
    }

    if (!paymentAccountId) {
      toastr.error('Payment account needs to be set');
      return;
    }

    setEmailing(true);

    if (!paymentSession) {
      try {
        await createPaymentSession();
      } catch (err) {
        console.error(err);
        toastr.error('Something went wrong creating payment session');
      }
    }

    ApiService.post('/payment/worldnet-hpp/email', {
      customerNum,
      customer,
      invoices: getInvoiceList(),
      email: hostedEmail,
      sessionId,
      accountId: paymentAccountId,
    })
      .then(() => {
        toastr.success(`Email Sent to ${hostedEmail}`);
        handleResume({ checkPaymentSession: false });
      })
      .catch(err => {
        toastr.error(`Something went wrong while trying to send email. Error ${err.message}`);
      })
      .finally(() => setEmailing(false));
  };

  const renderCashCollect = () => {
    if (!showCashForm || totalPaid >= amount || !customer || isSessionClosed() || isCredit() || readOnly) {
      return null;
    }

    return (
      <ListGroup.Item className="payment-list-item">
        <PaymentCashCollect
          amount={getOutstandingAmount()}
          customer={customer}
          customerNum={customerNum}
          department={department}
          invoices={getInvoiceList()}
          maxAmount={getOutstandingAmount()}
          onCollect={handleCashCollect}
          sessionId={sessionId}
          userInitials={userInitials}
        />
      </ListGroup.Item>
    );
  };

  const canComplete = () => {
    if (!isCredit()) {
      return totalPaid >= amount;
    }

    const totalRefunded = getTotalRefundedForSession();
    if (!amount && totalRefunded) {
      // amount is undefined / zero the user can submit with any refund amount > 0
      return true;
    }

    return totalRefunded >= Math.abs(amount);
  };

  const renderAction = () => {
    const buttons = [];

    buttons.push();

    if (canComplete()) {
      buttons.push(
        <Button
          key={0}
          variant="success"
          onClick={() => {
            const additionalOpts: any = {};
            if (selectCashInvoicePrinter && activeCashInvoicePrinterId !== null) {
              // we'll check for a name of "Screen" for ex-post screen print support
              additionalOpts.printer = {
                id: activeCashInvoicePrinterId,
                name: _.find(cashInvoicePrinters, { id: activeCashInvoicePrinterId })!.name,
              };
            }

            modal.close(); // close self first

            onSuccess({
              transactions: isCredit() ? getCreditTransactionSummary() : getTransactionSummary(),
              cash: getCashAmount(),
              sessionId,
              invoices: getInvoiceList(),
              branchName: branch.name,
              mvAccount: branch.mvAccount,
              ...additionalOpts,
            });
          }}
          disabled={!canComplete()}
          className="d-inline-block ml-1"
        >
          <i className="fas fa-check" /> &nbsp; Complete
        </Button>
      );
    } else {
      if (department !== 'service') {
        // resuming later will not be an expected feature for service dept
        buttons.push(
          <Button key={1} variant="warning" onClick={() => handleResume()} className="d-inline-block ml-1">
            Resume Later
          </Button>
        );
      }
    }

    return buttons;
  };

  const renderGatewaySelect = () => {
    if (!hasTerminalSupport() || !hasCardNotPresentSupport() || isCredit()) {
      // does not have an option
      return;
    }

    if (paymentSession && paymentSession.status !== 'open') {
      return;
    }

    return (
      <ListGroup.Item className="payment-gateway-select d-flex p-0">
        <div
          onClick={() => setMethod('cardNotPresent')}
          className={`text-center ${method === 'cardNotPresent' ? 'bg-primary text-white' : ''} p-2 flex-grow-1`}
        >
          <i className="fad fa-credit-card" /> &nbsp; Card Not Present
        </div>
        <div
          onClick={() => setMethod('terminal')}
          className={`text-center ${method === 'terminal' ? 'bg-primary text-white' : ''} p-2 flex-grow-1`}
        >
          <i className="fad fa-cash-register" /> &nbsp; Terminal
        </div>
        {hasHostedSupport() && (
          <div
            onClick={() => setMethod('hosted')}
            className={`text-center ${method === 'hosted' ? 'bg-primary text-white' : ''} p-2 flex-grow-1`}
          >
            <i className="fad fa-paper-plane" /> &nbsp; Email
          </div>
        )}
      </ListGroup.Item>
    );
  };

  const renderCancelButton = () => {
    if (isCredit()) {
      return null;
    }

    return (
      <Button variant="danger" onClick={handleCancel} className="d-inline-block float-left" disabled={!userInitials}>
        Cancel / Refund
      </Button>
    );
  };

  const renderOutstanding = () => {
    if (isCredit() && getOutstandingCreditAmount()) {
      return (
        <Button disabled variant="default">
          ${(getOutstandingCreditAmount() || 0).toFixed(2)} credit outstanding
        </Button>
      );
    }

    if (isPaid() || !isCredit()) {
      return;
    }

    return (
      <Button disabled variant="default">
        ${getOutstandingAmount().toFixed(2)} outstanding
      </Button>
    );
  };

  const renderAddTransactionButton = () => {
    if (showChargeForm || isPaid() || isSessionClosed() || isCredit() || readOnly) {
      return null;
    }

    return (
      <Button
        variant="primary"
        className="mr-2"
        onClick={() => {
          setShowChargeForm(true);
          setShowCashForm(false);
        }}
      >
        Add Charge
      </Button>
    );
  };

  const renderAddCashButton = () => {
    if (showCashForm || isCredit() || isPaid() || isSessionClosed() || readOnly || noCash || method === 'hosted') {
      return null;
    }

    return (
      <Button variant="success" onClick={handleAddCash}>
        Add Cash
      </Button>
    );
  };

  const renderTransactions = () => {
    return cardPayments
      .filter((tx: CardPayment) => tx.transactionType === 'sale')
      .map((tx: CardPayment, i: number) => (
        <ListGroup.Item key={i}>
          <PaymentCardTransaction
            paymentTransaction={tx}
            onSale={handleSale}
            onVoid={handleVoid}
            onRefund={handleRefund}
            userInitials={userInitials}
            showRefundForm={isCredit()}
            sessionId={sessionId}
            maxRefundAmount={isCredit() ? getOutstandingCreditAmount() : undefined}
          />
        </ListGroup.Item>
      ));
  };

  const renderCashTransactions = () => {
    return cashPayments
      .filter((tx: CashPayment) => tx.transactionType === 'sale')
      .map((tx: CashTransaction, i: number) => (
        <ListGroup.Item key={String(i)}>
          <PaymentCashTransaction
            transaction={tx}
            onRefund={handleCashRefund}
            sessionId={sessionId}
            showRefundForm={isCredit()}
            maxRefundAmount={isCredit() ? getOutstandingCreditAmount() : undefined}
          />
        </ListGroup.Item>
      ));
  };

  const renderChargeForm = () => {
    if (
      !showChargeForm ||
      totalPaid >= amount ||
      !customer ||
      isSessionClosed() ||
      isCredit() ||
      readOnly ||
      method === 'hosted'
    ) {
      return null;
    }

    return (
      <ListGroup.Item>
        <PaymentCardCharge
          customer={customer}
          customerNum={customerNum}
          department={department}
          invoices={getInvoiceList()}
          maxAmount={parseFloat((amount - totalPaid).toFixed(2))}
          method={method}
          onSale={handleSale}
          refId={getNextRefId()}
          sessionId={sessionId}
          showClose={!!cardPayments.length}
          terminalId={activeTerminalId}
          userInitials={userInitials}
        />
      </ListGroup.Item>
    );
  };

  const renderActionRow = () => {
    const defaultActions = (
      <Row>
        <Col md={6} className="text-left">
          {renderCancelButton()}
        </Col>
        <Col md={6} className="text-right">
          {renderAction()}
        </Col>
      </Row>
    );

    if (isCredit()) {
      return defaultActions;
    }

    if (isSessionClosed() || readOnly) {
      return (
        <Row>
          <Col className="text-center text-uppercase text-bold">Payment Session {paymentSession?.status}</Col>
        </Row>
      );
    }

    if (canceling) {
      return (
        <Row>
          <Col>
            <Button variant="danger" className="d-inline-block" disabled>
              <i className="fas fa-circle-notch fa-spin" /> &nbsp; Canceling Sale
            </Button>
          </Col>
        </Row>
      );
    }

    return defaultActions;
  };

  const renderInvoices = () => {
    const invoiceList = getInvoiceList();
    if (!invoiceList || !invoiceList.length) {
      return null;
    }

    return <b className="text-primary">{invoiceList.map(inv => inv.invoiceNum).toString()}</b>;
  };

  const renderSelectCashInvoicePrinter = () => {
    if (!selectCashInvoicePrinter || !cashInvoicePrinters.length || isSessionClosed() || readOnly) {
      return;
    }

    return (
      <ListGroup.Item className="form-select" style={{ backgroundColor: 'var(--yellow-0)' }}>
        <Row>
          <Col md={12}>
            <Form.Group>
              <label>Select Cash Invoice Printer</label>
              <Form.Control
                value={activeCashInvoicePrinterId ?? ''}
                onChange={e => setActiveCashInvoicePrinterId(e.target.value)}
                as="select"
              >
                {cashInvoicePrinters.map((p: Printer) => (
                  <option value={p.id} key={p.id}>
                    {p.name}
                  </option>
                ))}
              </Form.Control>
            </Form.Group>
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  const renderTerminalSelect = () => {
    if (isSessionClosed() || readOnly) {
      return null;
    }

    return (
      <ListGroup.Item className="form-select" style={{ backgroundColor: 'var(--yellow-0)' }}>
        <Row>
          {!isCredit() && method === 'terminal' && (
            <Col md={8}>
              <Form.Group>
                <label>Select Terminal</label>
                <Form.Control value={activeTerminalId} onChange={e => setActiveTerminalId(e.target.value)} as="select">
                  {!activeTerminalId && <option />}
                  {terminals.map((t: PaymentTerminal) => (
                    <option value={t.id} key={t.id}>
                      {t.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          )}
          {method === 'hosted' && !isCredit() && (
            <Col md={8}>
              <Form.Group>
                <label>Account</label>
                <Form.Control
                  onChange={e => setPaymentAccountId(parseInt(e.target.value))}
                  as="select"
                  value={paymentAccountId}
                >
                  <option></option>
                  {paymentAccounts.map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </Form.Control>
              </Form.Group>
            </Col>
          )}
          <Col md={4}>
            <Form.Group>
              <label>User Initials</label>
              <Form.Control value={userInitials} onChange={e => setUserInitials(e.target.value)} />
              {!userInitials && <Form.Text className="text-danger">Required</Form.Text>}
            </Form.Group>
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  const renderEmailLog = () => {
    if (!paymentSession || !paymentSession.emails || !paymentSession.emails.length) {
      return;
    }

    const rows = paymentSession.emails.map((entry, i) => (
      <tr key={i}>
        <td>{entry.address}</td>
        <td className="text-right">{entry.date}</td>
      </tr>
    ));

    return (
      <ListGroup.Item>
        <table className="table table-sm">
          <thead>
            <tr>
              <th colSpan={2}>Email Log</th>
            </tr>
          </thead>
          {rows}
        </table>
      </ListGroup.Item>
    );
  };

  const renderHostedActions = () => {
    if (isSessionClosed() || readOnly || isCredit()) {
      return;
    }

    return (
      <ListGroup.Item>
        <Row>
          <Col md={8}>
            <Form.Group>
              <label>Email</label>
              {department === 'parts' && (
                <CreatableSelect
                  isClearable
                  options={hostedEmailOptions.map(email => {
                    return { label: email, value: email };
                  })}
                  onChange={newValue => {
                    if (!newValue?.value || !/.+@.+/.test(newValue?.value || '')) {
                      setHostedEmail('');
                    }

                    setHostedEmail(newValue?.value || '');
                  }}
                />
              )}
              {department !== 'parts' && (
                <Form.Control value={hostedEmail} onChange={e => setHostedEmail(e.target.value)} />
              )}
            </Form.Group>
          </Col>
          <Col md={4}></Col>
        </Row>
        <Row>
          <Col md={12} className="text-right">
            <Button onClick={handleHostedEmail} disabled={emailing || !hostedEmail}>
              {emailing ? (
                <>
                  <ClipLoader size={13} color="white" />
                  <span className="pl-1">Sending Email</span>
                </>
              ) : (
                <div>Send Email</div>
              )}
            </Button>
          </Col>
        </Row>
      </ListGroup.Item>
    );
  };

  const renderHostedInfo = () => {
    if (method !== 'hosted') {
      return;
    }

    return (
      <>
        {renderEmailLog()}
        {renderHostedActions()}
      </>
    );
  };

  if (!ready || !customer) {
    return null;
  }

  const renderHeader = () => {
    if (isCredit()) {
      return (
        <>
          Refund{' '}
          <span className="text-bold">
            {customer.name} ({customerNum})
          </span>{' '}
          for invoice {renderInvoices()}
        </>
      );
    }

    return (
      <>
        <span className="text-bold">
          {customer.name} ({customerNum})
        </span>{' '}
        paying <span className="text-success text-bold">${amount.toFixed(2)}</span> for invoice {renderInvoices()}
      </>
    );
  };

  return (
    <>
      <Row>
        <Col md={12} className="p-3">
          <Card style={{ fontSize: '19px', backgroundColor: '#eee', borderRadius: '3px' }} className="mb-3">
            <Card.Body>{renderHeader()}</Card.Body>
          </Card>
          <div>
            <ListGroup className="mb-3">
              {renderGatewaySelect()}
              {renderSelectCashInvoicePrinter()}
              {renderTerminalSelect()}
              {renderTransactions()}
              {renderCashTransactions()}
              {renderCashCollect()}
              {renderChargeForm()}
              {renderHostedInfo()}
            </ListGroup>
            {renderAddTransactionButton()}
            {renderAddCashButton()}
            <div className="float-right">{renderOutstanding()}</div>
          </div>
        </Col>
      </Row>
      <Row>
        <Col>
          <hr />
        </Col>
      </Row>
      {renderActionRow()}
    </>
  );
};

export default PaymentSaleForm;
